import type { MenuLink } from '@iheartjane/data-access';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  Flex,
  Typography,
} from '@iheartjane/reefer';
import type { Nullish } from '@iheartjane/types';
import { clsx } from 'clsx';
import type { Dispatch, SetStateAction } from 'react';
import { useState } from 'react';

import styles from './mainMenu.module.css';

type MainMenuLevel = 'primary' | 'secondary' | 'tertiary';

type LinkProps = {
  expanded?: boolean;
  hasLinks?: boolean;
  item: MenuLink;
  level?: MainMenuLevel;
  setExpanded?: Dispatch<SetStateAction<boolean>>;
};

const Link = ({
  expanded,
  hasLinks,
  item,
  level = 'primary',
  setExpanded,
}: LinkProps) => {
  return (
    <>
      {item.destination ? (
        <a
          className={clsx(
            {
              [styles['mainMenuItem']]: level === 'primary',
              [styles['mainMenuSubItem']]: level === 'secondary',
              [styles['mainMenuTertiaryItem']]: level === 'tertiary',
            },
            styles['mainMenuItemShared']
          )}
          href={item.destination}
          target="_blank"
          rel="noopener noreferrer"
        >
          {item.label}
        </a>
      ) : (
        <Typography
          className={clsx(
            {
              [styles['mainMenuItem']]: level === 'primary',
              [styles['mainMenuSubItem']]: level === 'secondary',
              [styles['mainMenuTertiaryItem']]: level === 'tertiary',
            },
            styles['mainMenuItemShared']
          )}
        >
          {item.label}
        </Typography>
      )}

      {hasLinks && setExpanded && (
        <Flex
          ariaLabel={`${expanded ? 'Collapse' : 'Expand'} submenu items`}
          className={styles['chevronContainer']}
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? (
            <ChevronUpIcon className={styles['chevronIcon']} />
          ) : (
            <ChevronDownIcon className={styles['chevronIcon']} />
          )}
        </Flex>
      )}
    </>
  );
};

interface MainMenuItemProps {
  item: MenuLink;
  level?: MainMenuLevel;
}

function MainMenuItem({ item, level = 'primary' }: MainMenuItemProps) {
  const [expanded, setExpanded] = useState(false);

  const hasLinks = Boolean(item.links && item.links.length > 0);

  return (
    <>
      <div className={styles['parentLinkContainer']}>
        <Link
          item={item}
          level={level}
          hasLinks={hasLinks}
          expanded={expanded}
          setExpanded={setExpanded}
        />
      </div>
      {expanded &&
        item.links?.map((link, i) => (
          <MainMenuItem
            key={i}
            item={link}
            level={level === 'primary' ? 'secondary' : 'tertiary'}
          />
        ))}
    </>
  );
}

interface MainMenuItemsProps {
  items: Nullish<MenuLink[]>;
}

export const MainMenuItems = ({ items }: MainMenuItemsProps) => (
  <Flex flexDirection="column">
    {(items ?? []).map((item, i) => (
      <MainMenuItem key={i} item={item} />
    ))}
  </Flex>
);
